import { useCallback, useRef } from "react";
import { ContactDetailsBlockType } from "../types";
import { TellescopeModelFromName, useTellescopeMutation, useTellescopeQueryClient } from "@tellescope/tanstack-query";
import { useWritableViewId, UseWritableViewIdArgs, WritableView } from "./use-writable-view-id";

export interface UseCreateBlocksArgs {
  viewId: string;
  onViewMaterialized?: UseWritableViewIdArgs["onViewMaterialized"];
}

export const useCreateBlocks = ({ viewId, onViewMaterialized }: UseCreateBlocksArgs) => {
  const { getQueryData } = useTellescopeQueryClient();

  /**
   * Matches the sibling config in `useBlockMutations`, and the optimistic half is load-bearing rather
   * than cosmetic: `optimisticUpdates` is what makes the added block reach the cache synchronously.
   * Without it the cache does not carry the new block until the server responds, and `writeBlockInfo`
   * rebuilds its payload from that cache at commit time — so a block-info debounce firing inside that
   * window builds on an array missing the new block and, writing with `replaceObjectFields: true`,
   * erases it. Writing the cache up front closes the window without a cross-block flush registry: a
   * block can only flush its own debounce, while a block-add contends with every mounted block's.
   */
  const updateViewMutation = useTellescopeMutation({
    model: "custom_dashboards",
    method: "updateOne",
    optimisticUpdates: true,
    autoCacheUpdateStrategy: "update",
  });

  const resolveWritableViewId = useWritableViewId({ viewId, onViewMaterialized });

  type MutateOptions = Parameters<typeof updateViewMutation.mutateAsync>[1];
  type CreateBlocksOptions = NonNullable<MutateOptions> & {
    replace?: boolean;
  };

  const pendingAppendRef = useRef<
    | {
        viewId: string;
        append: Promise<TellescopeModelFromName<"custom_dashboards">>;
      }
    | undefined
  >(undefined);

  /**
   * The blocks a new block should be appended to, for the view actually being written.
   *
   * Read from the query cache rather than from the render-time `resolvedView`, and keyed to
   * `writableViewId` rather than to the `viewId` *prop*. Those two name different views during the
   * first-time flow — the prop is still the placeholder sentinel while the write targets the record
   * that was just materialized — and every write here uses `replaceObjectFields: true`, so a base
   * taken from the wrong view replaces the target's blocks wholesale. This mirrors `resolveSiblings`
   * in use-block-mutations.ts, which documents the same rule: the cache is the only source that is
   * current mid-await, and callers must resolve after their await, never before.
   *
   * `created` is only a fallback for the window where the materialized record has not reached the
   * cache yet: its blocks are authoritative because this call is what created it, but only until a
   * later write lands. It is a snapshot taken at creation and `useWritableViewId` hands back the same
   * one for as long as the `viewId` prop is still the placeholder, so preferring it over the cache
   * would make every add after the first rebuild from the view's original blocks and — writing with
   * `replaceObjectFields: true` — erase the adds in between. The cache wins whenever it has the
   * record; `created` covers only the miss, where it is the sole authoritative source.
   *
   * Returns `undefined` when no authoritative base exists, which makes the append reject rather than
   * write: building on an assumed-empty array would replace the view's saved blocks.
   */
  const resolveBaseBlocks = useCallback(
    ({ id, created }: WritableView) => {
      return getQueryData({ model: "custom_dashboards", method: "getOne", request: id })?.blocks ?? created?.blocks;
    },
    [getQueryData]
  );

  return useCallback(
    (blockTypes: ContactDetailsBlockType[], { replace = false, ...options }: CreateBlocksOptions = {}) => {
      const previousAppend = pendingAppendRef.current;

      const append = (async () => {
        const writableView = await resolveWritableViewId();

        const newBlocks = blockTypes.map<TellescopeModelFromName<"custom_dashboards">["blocks"][number]>((type) => ({
          type,
          info: {
            id: crypto.randomUUID(),
          },
        }));

        // A replace discards the current blocks, so it needs no base to build on — but it still joins
        // the pending chain, so a later append cannot resolve a base from before it landed.
        if (!replace) {
          // Chained only when the prior append targeted this same view: the view id can change under a
          // mounted hook, and every write here uses `replaceObjectFields: true`, so carrying another
          // view's blocks over would replace this one's wholesale.
          // A failed predecessor falls back to the cache rather than failing in sympathy: one add that
          // could not be written should not stop the next from succeeding on its own base.
          const previousBlocks =
            previousAppend?.viewId === viewId
              ? await previousAppend.append.then(
                  (updated) => updated.blocks,
                  () => undefined
                )
              : undefined;
          // Resolved only after both awaits, so a sibling write that landed while we waited is included.
          const existingBlocks = previousBlocks ?? resolveBaseBlocks(writableView);
          // Rejects rather than resolving: a caller cannot distinguish `undefined` from a successful
          // write, so resolving here would report success for blocks that were never saved.
          if (!existingBlocks) throw new Error(`No authoritative base blocks for view ${writableView.id}`);

          newBlocks.unshift(...existingBlocks);
        }

        return updateViewMutation.mutateAsync(
          {
            id: writableView.id,
            blocks: newBlocks,
            _options: {
              replaceObjectFields: true,
            },
          },
          options
        );
      })();

      const pending = { viewId, append };
      pendingAppendRef.current = pending;
      void append
        .catch(() => undefined)
        .finally(() => {
          if (pendingAppendRef.current === pending) pendingAppendRef.current = undefined;
        });

      return append;
    },
    [resolveBaseBlocks, resolveWritableViewId, updateViewMutation, viewId]
  );
};
